/**
 * Rendering. Plain DOM — this view is a table and a status line, and a framework would
 * be more machinery than the thing it renders.
 */
import type { ConnState } from "./connection";
import { Fleet, Robot, isStale, sortedRobots } from "./fleet";
import { byGroup, cachedAnswer, fleetCalls, freshness, humanAge, isCacheable,
         objectsWith, pickerOptions, robotCalls, robotParam, type CacheRecord,
         type DebugCall, type DebugState, type Pending,
         type ParamSchema, type PickerOption } from "./debugQuery";
import { breadcrumb, formatHash, storeIdFor, type Nav } from "./nav";
import { COMMANDS_ENABLED, actionViews, canPress, missingArgs,
         type ActionView, type CommandAttempt } from "./commands";
import { floorsOf, readAreas, readPoints, type Pt } from "./mapPoints";
import { robotColour } from "./robotColour";
import { poseWhy } from "./poseWhy";
import { extentMetres, headingOnCanvas, impliedResolution, inBounds, isSquare,
         occupiedExtentPx, pixelToWorld, readMapFrame, withResolution, worldToPixel,
         type MapFrame } from "./mapFrame";
import { fitView, measure, panBy, toCanvas, toImage, zoomAt, type View } from "./mapView";

const el = (id: string) => document.getElementById(id)!;

export function showLogin(): void {
  el("login").classList.remove("hidden");
  el("app").classList.add("hidden");
}

/**
 * Render a failure ON THE LOGIN CARD.
 *
 * The status line lives inside the app section, which is hidden while signed out — so
 * reporting a failed sign-in there produces a login screen that refuses to advance and
 * explains nothing. This is the only place a pre-login error can be seen.
 */
export function showLoginError(summary: string, fix: string, raw?: string): void {
  const box = el("login-error");
  box.innerHTML =
    `<b>${escapeHtml(summary)}</b><br>${escapeHtml(fix)}` +
    (raw ? `<br><br><code>${escapeHtml(raw)}</code>` : "");
  box.classList.remove("hidden");
}

export function clearLoginError(): void {
  el("login-error").classList.add("hidden");
}

/** Which tenant/app/broker this build points at — the first thing to check when a
 *  login misbehaves, and invisible otherwise because it is baked in at build time. */
export function showDiagnostics(lines: string[]): void {
  el("login-diag").textContent = lines.join(" · ");
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

export function showApp(who: string): void {
  el("login").classList.add("hidden");
  el("app").classList.remove("hidden");
  el("who").textContent = who;
}

export function setStatus(state: ConnState, detail?: string): void {
  const dot = el("conn-dot");
  const text = el("conn-text");
  dot.className = `dot ${state}`;
  const label: Record<ConnState, string> = {
    connecting: "connecting",
    connected: "live",
    reconnecting: "reconnecting",
    unauthorized: "signed out",
    rejected: "refused by broker",
    offline: "offline",
  };
  text.textContent = detail ? `${label[state]} — ${detail}` : label[state];
}

function battery(r: Robot): string {
  // Absent means NOT REPORTED. "—" rather than 0%, which would read as a flat battery.
  if (typeof r.batteryPct !== "number") return "—";
  return `${Math.round(r.batteryPct)}%`;
}

function position(r: Robot): string {
  if (!r.position) return "—";
  const { x, y, floor, frame } = r.position;
  // Shown as bare numbers, never plotted: a vendor's frame (`keenon_map`) is not a
  // Norbot map, and drawing one on ours would imply a shared coordinate system. The
  // frame is labelled when it is NOT ours, so the reader can see why there is no map.
  const fl = floor ? ` · fl ${floor}` : "";
  const fr = frame && frame !== "map" ? ` (${frame})` : "";
  return `${x.toFixed(1)}, ${y.toFixed(1)}${fl}${fr}`;
}

// Modes that mean the robot is WORKING — out on the floor, not available. Deliberately
// platform-neutral: `patrol` is a Go2's job and `cleaning` a Keenon's, and both belong
// here. `charging` does not (committed, but not out) and neither does `manual` (a human
// has it). This set is the only place the fleet view judges a mode, and it reads the
// fleet vocabulary rather than any one vendor's enum.
const WORKING_MODES = new Set(["cleaning", "patrol", "mapping", "docking"]);

function stateBadge(r: Robot, now: number): string {
  if (isStale(r, now)) return `<span class="badge stale">stale</span>`;
  if (r.fault?.scram) return `<span class="badge fault">E-STOP</span>`;
  if (r.fault?.faulting) return `<span class="badge fault">fault</span>`;
  if (r.online === false) return `<span class="badge off">offline</span>`;
  const mode = r.mode ?? "unknown";
  // The lifecycle is shown only when it changes the meaning. `paused` is the difference
  // between a robot working and one stopped mid-job, which the mode alone cannot say.
  const label = r.modeState === "paused" ? `${mode} · paused` : mode;
  const cls = WORKING_MODES.has(mode) && r.modeState !== "paused" ? "work" : "idle";
  return `<span class="badge ${cls}">${label}</span>`;
}


/** A connection failure, shown WITHOUT signing the user out. */
export function showConnError(summary: string, fix: string): void {
  el("conn-error-title").textContent = summary;
  el("conn-error-fix").textContent = fix;
  el("conn-error").classList.remove("hidden");
}

export function clearConnError(): void {
  el("conn-error").classList.add("hidden");
}

export function showNoAccess(show: boolean): void {
  el("no-access").classList.toggle("hidden", !show);
}

/**
 * How old the underlying STATE is, as opposed to how long since a message arrived.
 *
 * These differ because polling is request-driven: a fleet listing can arrive this second
 * describing a robot as it was twenty minutes ago, and the Age column — which measures
 * arrival — would show 0s for it. Showing only that would present stale data as live,
 * which is the one thing a fleet view must never do.
 *
 * "live" means the row was last updated by a push rather than a poll: a webhook fires on
 * change, so its arrival time IS the state's time.
 */
function asOf(r: Robot, now: number): string {
  if (r.source && r.source.startsWith("webhook")) return "live";
  if (r.asOf === undefined) return "—";
  const secs = Math.max(0, Math.round((now - r.asOf) / 1000));
  if (secs < 90) return `${secs}s`;
  if (secs < 5400) return `${Math.round(secs / 60)}m`;
  return `${Math.round(secs / 3600)}h`;
}

// ---- the mock harness panel -------------------------------------------------

/**
 * Render the harness controls, or hide them entirely.
 *
 * `state` null means "configured but not reachable" — the normal resting case, because
 * the mock is stopped between sessions and fly starts it on the next request. That reads
 * as an invitation to activate it, not as an error.
 */
export function renderMockPanel(
  state: { scenarioId: string; periodS: number; robots: Record<string, { lifecycle: string; t: number; taskState?: string }> } | null,
  opts: { configured: boolean; busy?: boolean; error?: string },
): void {
  const panel = el("mock-panel");
  panel.classList.toggle("hidden", !opts.configured);
  if (!opts.configured) return;

  el("mock-error").classList.toggle("hidden", !opts.error);
  if (opts.error) el("mock-error").textContent = opts.error;

  for (const id of ["mock-wake", "mock-sleep", "mock-refresh"]) {
    (el(id) as HTMLButtonElement).disabled = !!opts.busy;
  }

  if (!state) {
    el("mock-summary").textContent = opts.busy ? "starting…" : "stopped — Activate to start it";
    el("mock-robots").textContent = "";
    return;
  }

  el("mock-summary").textContent =
    `${state.scenarioId} · ${state.periodS}s cycle`;

  const rows = Object.entries(state.robots).map(([sn, r]) => {
    // ACTIVE / DORMANT / ABSENT are worth spelling out rather than showing a toggle:
    // "dormant" (still answering polls, not advancing) is a genuinely different thing
    // from "absent" (gone from the robot list), and a single on/off would hide that.
    const cls = r.lifecycle === "active" ? "ok" : r.lifecycle === "dormant" ? "warn" : "bad";
    const task = r.taskState ? ` · ${r.taskState}` : "";
    return `<div class="mono"><span class="dot ${cls}"></span> ${sn} — `
         + `<b>${r.lifecycle.toUpperCase()}</b> · t=${r.t.toFixed(0)}s${task}</div>`;
  });
  el("mock-robots").innerHTML = rows.join("") || '<span class="mono">no robots</span>';
}

// ---- vendor debug surface (CR 008) ------------------------------------------
//
// Every control below is built from the adapter's retained manifest. Nothing here names
// a call, a parameter or an endpoint: that is what makes a call added to the adapter
// appear in this panel with no change to this file.

/** Which call the operator has open, and what they have typed into it. */
export interface DebugSelection {
  siteId: string;
  call: string;
  values: Record<string, string>;
}

export function renderDebugPanel(
  state: DebugState,
  sel: DebugSelection | null,
  /** siteId -> robot ids, from the RETAINED MQTT roster. Passed in rather than read
   *  here so this stays a pure render of what it is handed, and so a picker never
   *  becomes a reason to make a vendor call. */
  roster: (siteId: string) => string[],
  opts: { error?: string; onSelect: (siteId: string, call: string) => void;
          onField: (name: string, value: string) => void; onSend: () => void;
          /** Re-ask one call with `force: true`. Named by call rather than by request id
           *  because the answer being refreshed may have come from the shared cache and
           *  belong to somebody else's request. */
          onRefresh?: (call: string) => void },
): void {
  // FLEET-LEVEL CALLS ONLY. Anything addressed to a robot lives in that robot's
  // drill-down, where the serial is filled in for you — so there is one list per
  // question rather than the same call in two places with different ergonomics.
  const sites = [...state.manifests.keys()].sort()
    .filter((s) => fleetCalls(state.manifests.get(s)!).length > 0);
  debugPanelHasContent = sites.length > 0;

  const panel = el("debug-panel");
  panel.classList.toggle("hidden", !debugPanelHasContent);
  if (!debugPanelHasContent) return;

  el("debug-summary").textContent = sites
    .map((s) => {
      const m = state.manifests.get(s)!;
      const shown = fleetCalls(m);
      return `${s}: ${shown.filter((c) => c.enabled).length}/${shown.length} fleet calls` +
             (m.adapter?.version ? ` (adapter ${m.adapter.version})` : "");
    })
    .join("  ·  ");

  // Buttons change only when the manifest or the selection does.
  repaint("debug-calls",
          `${sites.join(",")}|${sel ? `${sel.siteId}/${sel.call}` : ""}`,
          (host) => {
    for (const siteId of sites) {
      for (const [group, entries] of byGroup({ calls: fleetCalls(state.manifests.get(siteId)!) })) {
        const row = document.createElement("div");
        row.style.margin = "4px 0";
        row.innerHTML = `<span class="mono" style="opacity:.6;display:inline-block;` +
                        `min-width:80px">${escapeHtml(group)}</span>`;
        for (const c of entries) {
          row.appendChild(callButton(c, !!sel && sel.siteId === siteId && sel.call === c.name,
                                     () => opts.onSelect(siteId, c.name)));
        }
        host.appendChild(row);
      }
    }
  });

  // The form is rebuilt ONLY when a different call is selected. Not on the tick, not on
  // a vendor push, and not on a keystroke — otherwise it cannot be typed into.
  const call = sel
    ? state.manifests.get(sel.siteId)?.calls.find((c) => c.name === sel.call)
    : null;
  // The signature gains the OPTION COUNTS, not the options. A picker that gains its
  // first entries must rebuild to show them; rebuilding whenever a label changed would
  // put us back to a form that cannot be typed into. Counts change rarely — when a
  // store list first arrives, or a robot joins — and never on a keystroke.
  const optionSig = sel && call
    ? Object.values(call.params ?? {})
        .map((sc) => pickerOptions(sc["x-ui"], state, sel.siteId, roster(sel.siteId)).length)
        .join(",")
    : "";
  repaint("debug-form", sel && call ? `${sel.siteId}/${sel.call}/${optionSig}` : "", (host) => {
    if (sel && call) {
      host.appendChild(callForm(call, sel.values, null, opts.onField, opts.onSend,
                                (hint) => pickerOptions(hint, state, sel.siteId,
                                                        roster(sel.siteId))));
    }
  });

  el("debug-error").classList.toggle("hidden", !opts.error);
  if (opts.error) el("debug-error").textContent = opts.error;

  // Fleet-level answers only — a robot's own queries belong in its drill-down.
  renderLog("debug-log", state.log.filter((p) => !p.robotId),
            (name) => sel ? state.manifests.get(sel.siteId)?.calls.find((c) => c.name === name)
                          : undefined,
            opts.onRefresh);
}

// ---- drill-down: one robot ---------------------------------------------------

/**
 * Exactly one of the three screens, and the others HIDDEN rather than scrolled past.
 *
 * They answer the same questions about different subjects, so leaving two on screen
 * invites reading a number from the wrong one. Stated as one function taking the whole
 * picture rather than a toggle per section, because with three views the "which of the
 * others did I remember to hide" bug becomes a matter of time.
 */
export function showScreens(want: { detail?: boolean; guided?: boolean }): void {
  const detail = !!want.detail;
  const guided = !!want.guided;
  el("detail").classList.toggle("hidden", !detail);
  el("guided").classList.toggle("hidden", !guided);
  // The vendor panel belongs to the SITE screen, not to the drill-down: it is
  // site-scoped, and a robot's own calls have their own surface inside the detail view.
  el("debug-panel").classList.toggle("hidden", detail || !debugPanelHasContent);
}

let debugPanelHasContent = false;

/** One call button, shared by both views so they cannot drift in behaviour. */
function callButton(c: DebugCall, selected: boolean, onPick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = "ghost";
  // THE REGISTRY NAME, verbatim and monospaced — not the friendly title.
  //
  // This surface is for programmers holding the vendor's PDF. `area_list` is what you
  // send as `call`, what `keenon_adapter debug` takes, and what you grep the registry
  // for; "Cleaning areas" is none of those, and a reader looking for `area_list` and
  // finding only "Cleaning areas" concludes the call is missing.
  b.textContent = c.name;
  b.style.cssText = "margin-right:6px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace";

  // Everything known about the call, on hover. `title` rather than a custom tooltip:
  // it survives every browser and needs no positioning logic, and this is a debug panel.
  const lines = [
    c.title ? `${c.name} — ${c.title}` : c.name,
    // No vendor path here, deliberately: the manifest withholds it, because this panel
    // runs in a browser and every subscriber on the site would see our integration's
    // shape. `keenon_adapter debug list` on the adapter prints paths.

    c.doc ? `\n\n${c.doc}` : "",
    c.min_interval_ms ? `\n\nRate limit: one call every ` +
      `${(c.min_interval_ms / 1000).toFixed(1)}s` : "",
    c.result === "artifact_ref" ? `\nReturns binary — stored, not inlined` : "",
    c.enabled ? "" : `\n\nDISABLED: ${c.reason ?? "no reason given"}`,
  ];
  b.title = lines.filter(Boolean).join("");

  // A disabled call stays VISIBLE with its reason: hiding it would make "this adapter
  // cannot do that" indistinguishable from "that does not exist".
  b.disabled = !c.enabled;
  if (selected) b.style.fontWeight = "700";
  b.onclick = onPick;
  return b;
}

/**
 * The arguments of a command, in words, for the confirmation line.
 *
 * Labels rather than ids wherever the schema gave a picker a list to choose from: an
 * operator approving a cleaning run should read "Lobby, Corridor", not two uuids they
 * cannot check. A count for the list fields, because five area names do not fit on one
 * line and "5 areas" is the number that matters when deciding whether to press.
 */
function describeArgs(v: ActionView, values: Record<string, string>): string {
  const bits: string[] = [];
  for (const [name, schema] of Object.entries(v.params) as Array<[string, ParamSchema]>) {
    const raw = String(values[name] ?? "").trim();
    if (!raw) continue;
    const title = schema.title || name;
    if (name === "areaIdList") {
      const n = raw.split(",").filter((x) => x.trim()).length;
      bits.push(`${n} area${n === 1 ? "" : "s"}`);
      continue;
    }
    bits.push(`${title}: ${raw}`);
  }
  return bits.join(", ");
}

/** Pickers whose options come from a cached vendor answer, and can therefore be
 *  refetched. An unknown hint is not one of these and still degrades to a text box. */
const KNOWN_PICKERS = new Set(["area-picker", "clean-model-picker", "back-point-picker",
                               "schedule-picker", "store-picker", "robot-picker",
                               "scene-picker"]);

function refreshButton(hint: string, onRefresh: (hint: string) => void): HTMLElement {
  const b = document.createElement("button");
  b.className = "act";
  b.style.cssText = "padding:2px 6px;margin-left:6px;font-size:11px";
  b.textContent = "\u21bb";
  b.title = "fetch this list again from the vendor";
  b.onclick = (ev) => { ev.preventDefault(); onRefresh(hint); };
  return b;
}

/** The form for one call, omitting any parameter the view already knows. */
function callForm(
  call: DebugCall, values: Record<string, string>, skip: string | null,
  onField: (name: string, value: string) => void, onSend: () => void,
  pick?: (hint: string | undefined) => PickerOption[],
  /** False where the caller renders its own send control — the command surface puts the
   *  arm/confirm state on that button, which this function knows nothing about. */
  withSend = true,
  /** Re-fetch the list behind a picker. Offered beside any field whose options come from
   *  a cached vendor answer, because an empty dropdown otherwise has no way forward. */
  onRefreshOptions?: (hint: string) => void,
): DocumentFragment {
  const frag = document.createDocumentFragment();
  const required = new Set(call.required ?? []);
  for (const [name, schema] of Object.entries(call.params ?? {})) {
    // The robot is not asked for in a robot's own view: the operator is already looking
    // at one, and retyping a serial is both tedious and the likeliest way to query the
    // wrong machine.
    if (name === skip) continue;
    const wrap = document.createElement("label");
    wrap.style.cssText = "display:inline-block;margin:0 10px 6px 0";
    // The PARAMETER NAME, verbatim, for the same reason the button shows the call name:
    // it is what the vendor's docs call it and what you would type into the CLI. The
    // friendly title rides along after it.
    const req = required.has(name);
    const label = `${name}${req ? " *" : ""}${schema.title ? ` (${schema.title})` : ""}`;
    wrap.innerHTML = `<span class="mono" style="opacity:.7">${escapeHtml(label)}</span><br>`;

    // A PICKER WHEN WE CAN FILL ONE, a text box otherwise. `x-ui` is a hint and never a
    // contract, so an unrecognised one and an empty option list take the same path: a
    // picker with nothing in it is a dead end, where a text box is merely inconvenient.
    const hint = schema["x-ui"];
    const current = values[name] ?? "";

    // A CLOSED SET is always a dropdown, whatever else is going on: asking somebody to
    // type one of three known values invites the fourth.
    if (Array.isArray(schema.enum) && schema.enum.length) {
      const sel = document.createElement("select");
      const labels = schema["x-labels"] ?? {};
      if (!req) sel.add(new Option("\u2014 none \u2014", ""));
      for (const v of schema.enum) {
        const key = String(v);
        sel.add(new Option(labels[key] ?? key, key));
      }
      sel.value = current || (req ? String(schema.enum[0]) : "");
      sel.title = `${name}: one of ${schema.enum.join(", ")}`;
      sel.onchange = () => onField(name, sel.value);
      wrap.appendChild(sel);
      frag.appendChild(wrap);
      continue;
    }

    // A CHECKBOX for a boolean. Rendering it as a text box asked somebody to type the
    // word "true", which is both tedious and a type error waiting to happen — the form
    // would have sent the STRING "true" where the schema declares a boolean.
    if (schema.type === "boolean") {
      const cb = document.createElement("input");
      cb.type = "checkbox";
      // The declared default when nothing has been chosen, so the commonest case needs
      // no decision — `enabled` on a schedule means "on" unless you say otherwise.
      cb.checked = current ? current === "true" : schema.default === true;
      cb.title = `${name}: boolean`;
      // Written back as the canonical strings `coerceArgs` reads, rather than leaving
      // the value's shape to depend on which control produced it.
      cb.onchange = () => onField(name, cb.checked ? "true" : "false");
      wrap.appendChild(cb);
      frag.appendChild(wrap);
      continue;
    }

    // A REAL TIME PICKER, which also means the browser enforces hh:mm for us and the
    // operator gets their locale's clock rather than a format to guess at.
    if (hint === "time") {
      const t = document.createElement("input");
      t.type = "time";
      t.value = current;
      t.title = `${name}${req ? " (required)" : " (optional)"}`;
      t.onchange = () => onField(name, t.value);
      wrap.appendChild(t);
      frag.appendChild(wrap);
      continue;
    }

    const options = pick?.(hint) ?? [];
    if (options.length) {
      const sel = document.createElement("select");
      // A blank first entry, always selected when nothing has been chosen. Without it the
      // browser pre-selects the first real option, which silently commits the operator to
      // a value they never picked — and for an OPTIONAL parameter that turns "omit this"
      // into "send the first store in the list".
      sel.add(new Option(req ? "— choose —" : "— none —", ""));
      for (const o of options) sel.add(new Option(o.label, o.value));
      // A value already typed or carried in that the list does not contain must survive.
      // Dropping it would rewrite the operator's input on render, which is the worst kind
      // of helpfulness.
      if (current && !options.some((o) => o.value === current)) {
        sel.add(new Option(`${current} (not in list)`, current));
      }
      sel.value = current;
      sel.title = `${name}: ${schema.type ?? "string"}` +
                  (req ? " (required)" : " (optional — choose none to omit it)");
      sel.onchange = () => onField(name, sel.value);
      wrap.appendChild(sel);
      if (hint && onRefreshOptions) wrap.appendChild(refreshButton(hint, onRefreshOptions));
      frag.appendChild(wrap);
      continue;
    }

    // NO OPTIONS BUT A KNOWN HINT: the list simply has not been fetched. A text box with
    // no way forward is where that used to end; the refresh button is the way forward.
    if (hint && onRefreshOptions && KNOWN_PICKERS.has(hint)) {
      const note = document.createElement("span");
      note.className = "mono";
      note.style.cssText = "opacity:.7;margin-right:6px";
      note.textContent = "no list yet";
      wrap.appendChild(note);
      wrap.appendChild(refreshButton(hint, onRefreshOptions));
      frag.appendChild(wrap);
      continue;
    }

    const input = document.createElement("input");
    input.value = current;
    // Optional fields say so, because a blank box with no marking reads as required —
    // and leaving one blank is now the correct way to omit it.
    input.placeholder = req ? (schema.type ?? "required")
                            : `optional — blank to omit`;
    input.title = `${name}: ${schema.type ?? "string"}` +
                  (req ? " (required)" : " (optional — leave blank to omit it)");
    input.oninput = () => onField(name, input.value);
    wrap.appendChild(input);
    frag.appendChild(wrap);
  }
  if (withSend) {
    const send = document.createElement("button");
    send.className = "primary";
    send.textContent = `Send ${call.name}`;
    send.onclick = onSend;
    frag.appendChild(send);
  }
  return frag;
}

// ---- the result log ----------------------------------------------------------
//
// Rendered SEPARATELY from everything around it, and only when it has actually changed.
//
// The reason is a real annoyance rather than tidiness: a `<details>` element holds its
// open state in the DOM, so rebuilding innerHTML collapses it. The detail view re-renders
// every second and on every raw vendor push — which is the highest-rate channel there is
// — so an expanded response would snap shut a moment after you opened it, while you were
// reading it. The raw feed and the log now update independently of each other.
//
// Belt and braces, because "only when changed" is not enough: a NEW result legitimately
// rebuilds the list, and anything already open must survive that too. So open state is
// held here, keyed by request id, rather than trusted to the DOM.

const expanded = new Set<string>();

/**
 * Rebuild a subtree only when what it REPRESENTS changed.
 *
 * The forms are the reason this exists. Rebuilding innerHTML destroys the <input>
 * elements, and with them the focus, the caret and anything half-typed — so a form
 * repainted on the 1s tick (and on every raw vendor push, the highest-rate channel
 * there is) could not be typed into at all. You got about a second per click.
 *
 * The signature must describe the STRUCTURE, never the contents. Including the typed
 * values would rebuild on every keystroke, which is the same bug wearing a different
 * hat: the DOM already holds them, and they are pushed to the model by `oninput`.
 */
const paintSignature = new Map<string, string>();

function repaint(elementId: string, signature: string, build: (host: HTMLElement) => void): void {
  if (paintSignature.get(elementId) === signature) return;
  paintSignature.set(elementId, signature);
  const host = el(elementId);
  host.innerHTML = "";
  build(host);
}
/** Last rendered signature per element, so an unchanged log is not touched at all. */
const logSignature = new Map<string, string>();

function logEntryHtml(p: Pending, call?: DebugCall): string {
  if (!p.result) {
    return `<div style="opacity:.6">· ${escapeHtml(p.call)}` +
           `${p.forced ? " (refreshing)" : ""} — waiting…</div>`;
  }
  const r = p.result;
  // WHERE THE ANSWER CAME FROM, in place of the timing. A cache hit returns in
  // single-digit ms, so "3ms" was the only clue an operator had that they might be
  // looking at a day-old reading — and reading speed as freshness is exactly backwards.
  // `cached`/`age_ms` are stated by the adapter; see freshness().
  const chip = freshness(call, r);
  const took = chip
    ? ` · ${escapeHtml(chip)}`
    : r.elapsed_ms !== undefined ? ` ${r.elapsed_ms.toFixed(0)}ms` : "";
  if (!r.ok) {
    const code = r.vendor_code !== undefined ? ` [${escapeHtml(String(r.vendor_code))}]` : "";
    const sent = r.params
      ? Object.entries(r.params).map(([k, v]) => `${k}=${String(v)}`).join("  ")
      : "";
    const where = r.path
      ? ` title="${escapeHtml(`GET ${r.path}${sent ? `\n${sent}` : ""}`)}"` : "";
    return `<div style="color:var(--bad)"${where}>✗ ${escapeHtml(p.call)}${took}${code} — ` +
           `${escapeHtml(r.error ?? "failed")}</div>`;
  }
  // `open` comes from our own record, not from whatever the previous DOM happened to be.
  const open = expanded.has(p.requestId) ? " open" : "";
  // The SUMMARY stays minimal — call name and timing, so a log of twenty reads at a
  // glance. The endpoint goes INSIDE, where it is context for the payload you opened
  // rather than noise in a list you are scanning.
  // The request, reconstructed as one line: everything registered here is a GET, so the
  // parameters ARE the request. Shown as a query string rather than a JSON blob because
  // that is the shape you would paste into curl or compare against the vendor's docs.
  const q = r.params
    ? Object.entries(r.params)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&")
    : "";
  const path = r.path
    ? `<div style="opacity:.6;margin:2px 0 4px;word-break:break-all">` +
      `GET ${escapeHtml(r.path)}${q ? `?${escapeHtml(q)}` : ""}</div>`
    : "";
  // THE ARTIFACT REFERENCE, which is otherwise invisible: the response tree below shows
  // `data`, and for a binary result the bytes are deliberately not in it. Without this
  // line a map_fetch looks like a call that returned nothing but metadata, which is
  // exactly how it was first reported.
  const a = r.artifact;
  const artifact = !a ? "" : a.url
    ? `<div style="margin:2px 0 6px">` +
      `<a href="${escapeHtml(a.url)}" target="_blank" rel="noopener">open image</a>` +
      `<span class="mono" style="opacity:.7;margin-left:10px">` +
      `${a.image?.mode ?? "?"} ${a.image?.width ?? "?"}x${a.image?.height ?? "?"}` +
      ` · ${a.bytes !== undefined ? `${(a.bytes / 1024).toFixed(1)} KB` : "?"}` +
      ` · link expires</span></div>`
    : `<div class="mono" style="color:var(--bad);margin:2px 0 6px">` +
      `image not served: ${escapeHtml(a.error ?? "no url")}</div>`;
  // `refresh` re-asks the SAME call with force:true. The old answer stays on screen
  // while it is in flight — blanking it is how a refresh button becomes one people stop
  // pressing. Offered only for answers that could be cached; on an uncached call it
  // would just be a second Send.
  const refresh = r.cached || (call && isCacheable(call))
    ? ` <button data-refresh="${escapeHtml(p.call)}" ` +
      `style="font-size:11px;padding:0 5px">refresh</button>`
    : "";
  return `<details data-rid="${escapeHtml(p.requestId)}"${open}>` +
         `<summary>✓ ${escapeHtml(p.call)}${took}${refresh}</summary>${path}${artifact}` +
         `<pre style="white-space:pre-wrap;max-height:340px;overflow:auto">` +
         `${escapeHtml(JSON.stringify(r.data, null, 1) ?? "")}</pre></details>`;
}

/**
 * Paint one log, skipping the DOM entirely when nothing about it changed.
 *
 * The signature covers what is actually DISPLAYED — which entries, and whether each has
 * an answer yet. It deliberately does NOT include the open flags: those are applied to
 * the markup, and letting a toggle change the signature would rebuild the list on every
 * click, which is the very thing this exists to stop.
 */
function renderLog(
  elementId: string,
  entries: Pending[],
  lookup?: (name: string) => DebugCall | undefined,
  onRefresh?: (call: string) => void,
): void {
  // STRUCTURE, NEVER CONTENTS — and `age_ms` is the reason to say it again here. An age
  // that ticked would rebuild this list every second and take the caret out of whatever
  // form is open below it, which is the bug reported twice already. It cannot, because
  // the adapter reports age AT THE MOMENT THE ANSWER WAS SERVED: a fixed fact about one
  // answer, not a live counter. So the existing signature already covers it — a result
  // arriving flips `-` to `1`, and nothing about a finished row changes afterwards.
  const sig = entries.map((p) => `${p.requestId}:${p.result ? (p.result.ok ? "1" : "0") : "-"}`)
                     .join("|");
  if (logSignature.get(elementId) === sig) return;
  logSignature.set(elementId, sig);

  const box = el(elementId);
  box.innerHTML = entries.map((p) => logEntryHtml(p, lookup?.(p.call))).join("");
  box.querySelectorAll<HTMLDetailsElement>("details[data-rid]").forEach((d) => {
    d.ontoggle = () => {
      const rid = d.dataset.rid!;
      if (d.open) expanded.add(rid);
      else expanded.delete(rid);
      // NOT re-rendered here: the DOM already shows the right thing, and the signature
      // is unchanged, so the next tick leaves it alone.
    };
  });
  box.querySelectorAll<HTMLButtonElement>("button[data-refresh]").forEach((b) => {
    b.onclick = (ev) => {
      // The button lives inside a <summary>, whose default action is to toggle the
      // <details>. Refreshing would otherwise collapse the answer you are watching.
      ev.preventDefault();
      ev.stopPropagation();
      onRefresh?.(b.dataset.refresh!);
    };
  });
}

export interface DetailOpts {
  error?: string;
  onBack: () => void;
  onSelect: (call: string) => void;
  onField: (name: string, value: string) => void;
  onSend: () => void;
  onRefresh?: (call: string) => void;
}

export function renderDetail(
  robot: Robot | undefined, state: DebugState, selectedCall: string | null,
  values: Record<string, string>, now: number, opts: DetailOpts,
): void {
  (el("detail-back") as HTMLButtonElement).onclick = opts.onBack;
  if (!robot) {
    // The robot went away while we were looking at it — a fleet listing can drop a row.
    // Said plainly rather than rendering an empty shell that looks like a load failure.
    el("detail-title").textContent = "robot no longer in the fleet";
    el("detail-sub").textContent = "";
    el("detail-stats").innerHTML = "";
    return;
  }

  el("detail-title").textContent = robot.robotId;
  el("detail-sub").textContent =
    `${robot.siteId}${robot.model ? ` · ${robot.model}` : ""}`;

  // --- stats. Counts are session totals, so the raw ring turning over does not reset
  //     them; the ring length is shown separately because they answer different questions.
  const counts = robot.rawCounts ?? {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const parts = Object.entries(counts).sort()
    .map(([k, n]) => `${escapeHtml(k)} <b>${n}</b>`);
  el("detail-stats").innerHTML =
    `<div>${stateBadge(robot, now)} &nbsp; battery <b>${battery(robot)}</b> ` +
    `&nbsp; position <span class="mono">${position(robot)}</span></div>` +
    `<div style="margin-top:6px" class="mono">vendor pushes this session: ` +
    `${parts.join(" &nbsp; ") || "none yet"} &nbsp; (total <b>${total}</b>)</div>` +
    `<div style="margin-top:4px" class="mono" style="opacity:.7">last message ` +
    `${Math.round((now - robot.lastSeen) / 1000)}s ago` +
    (robot.fault?.detail ? ` &nbsp; fault: ${escapeHtml(String(robot.fault.detail))}` : "") +
    `</div>`;

  // --- this robot's calls, derived from the manifest rather than listed here
  const m = state.manifests.get(robot.siteId);
  const calls = m ? robotCalls(m) : [];
  el("detail-debug-summary").textContent = m
    ? `${calls.filter((c) => c.enabled).length}/${calls.length} robot calls`
    : "no adapter manifest for this site";

  repaint("detail-calls",
          `${robot.siteId}/${robot.robotId}|${calls.map((c) => c.name).join(",")}|${selectedCall ?? ""}`,
          (host) => {
    for (const c of calls) {
      host.appendChild(callButton(c, c.name === selectedCall, () => opts.onSelect(c.name)));
    }
  });

  const call = calls.find((c) => c.name === selectedCall);
  // Same rule as the fleet panel: identity and option COUNTS, never the typed values.
  //
  // The robot picker is skipped here anyway — `robotParam(call)` is already filled in
  // from the row you clicked — but a store or scene parameter on a robot-scoped call
  // still gets one.
  const optionSig = call
    ? Object.values(call.params ?? {})
        .map((sc) => pickerOptions(sc["x-ui"], state, robot.siteId,
                                   [robot.robotId]).length)
        .join(",")
    : "";
  repaint("detail-form",
          call ? `${robot.siteId}/${robot.robotId}/${call.name}/${optionSig}` : "", (host) => {
    if (call) {
      host.appendChild(callForm(call, values, robotParam(call), opts.onField, opts.onSend,
                                (hint) => pickerOptions(hint, state, robot.siteId,
                                                        [robot.robotId])));
    }
  });

  el("detail-error").classList.toggle("hidden", !opts.error);
  if (opts.error) el("detail-error").textContent = opts.error;

  // Only this robot's queries. A shared channel carries everyone's, and showing another
  // robot's answer here would be actively misleading.
  renderLog("detail-log",
            state.log.filter((p) => p.siteId === robot.siteId && p.robotId === robot.robotId),
            (name) => state.manifests.get(robot.siteId)?.calls.find((c) => c.name === name),
            opts.onRefresh);

  renderRawFeed(robot);
}

/**
 * The raw feed, newest first. Independent of the log above it in both directions: a new
 * vendor push repaints this and leaves an expanded response alone, and expanding a
 * response does not repaint this.
 *
 * Skipped entirely when nothing arrived, so the 1s tick does not rebuild a list of 200
 * rows — and, more to the point, does not fight whatever the reader is doing with it.
 */
let rawSignature = "";

function renderRawFeed(robot: Robot): void {
  const box = el("detail-raw");
  const records = robot.raw ?? [];
  // Newest arrival time plus count identifies the ring's state: the ring only ever gains
  // at the front and drops from the back.
  const sig = `${robot.siteId}/${robot.robotId}|${records.length}|${records[0]?.at ?? 0}`;
  if (sig === rawSignature) return;
  rawSignature = sig;

  const follow = (el("detail-raw-follow") as HTMLInputElement).checked;
  const wasAt = box.scrollTop;

  el("detail-raw-summary").textContent =
    records.length ? `${records.length} most recent (newest first)` : "nothing yet";

  box.innerHTML = records.map((r) => {
    const t = new Date(r.at).toISOString().slice(11, 23);
    const body = JSON.stringify(r.payload);
    return `<div style="border-bottom:1px solid rgba(128,128,128,.15);padding:2px 0">` +
           `<span style="opacity:.55">${t}</span> ` +
           `<span style="opacity:.8">${escapeHtml(r.source)}</span><br>` +
           `<span style="word-break:break-all">${escapeHtml(body ?? "")}</span></div>`;
  }).join("");

  // Newest is at the TOP, so following means staying at the top. Not following means
  // holding position — otherwise reading an older record is impossible while traffic
  // arrives, which is exactly when someone is reading it.
  box.scrollTop = follow ? 0 : wasAt;
}

/** Opening a different robot must not inherit the previous one's paint state. */
export function resetDetailRenderState(): void {
  rawSignature = "";
  logSignature.delete("detail-log");
  paintSignature.delete("detail-calls");
  paintSignature.delete("detail-form");
  // `expanded` is deliberately NOT cleared: it is keyed by request id, so a response you
  // left open is still open if you come back to that robot.
}

// ---- the guided flow (CR 008 Phase 6 step 5) ---------------------------------
//
// `stores -> robots -> robot`, reached from the "v2 test" link rather than being the
// landing screen. The robot step reuses `renderDetail` above: it is already the right
// screen, and a second copy of it would be a second thing to keep correct.
//
// EVERY SCREEN PREFERS DATA IT ALREADY HAS. The store list comes from the retained
// answer cache, which arrives on subscribe; the robot list comes from the retained MQTT
// roster. Opening the flow therefore costs ZERO vendor calls, which on a surface whose
// penalty for over-polling is a disabled client_id is not an optimisation but the design.

export interface GuidedOpts {
  onGo: (nav: Nav) => void;
  /** Fetch the scene list for the site being viewed. */
  onLoadScenes: () => void;
  /** The chosen scene and floor. A null FLOOR shows no map, and that is the initial
   *  state on purpose: `map_fetch` is the most expensive call on this surface and
   *  nobody should spend one merely by opening a site. */
  scene: string | null;
  floor: string | null;
  onScene: (code: string | null) => void;
  onFloor: (floor: string | null) => void;
  /** Fetch the named cleaning areas for this site. Names only — see readAreas. */
  onLoadAreas: () => void;
  /** Robots whose location is being polled and drawn on the map. */
  polled: Set<string>;
  onPolled: (robotId: string, on: boolean) => void;
  pollSeconds: number;
  /** The map frame in force, when there is one, so a robot's row can show the PIXEL its
   *  metre position lands on. A plausible metre coordinate with an impossible pixel is a
   *  units bug rather than a lost robot, and only the pair reveals it. */
  frame?: MapFrame | null;
  error?: string;
}

/** `cached 4m ago`, or "" when nothing is cached. Reads from the retained entry, so it
 *  says the same thing in every operator's browser. */
function cacheChip(rec: CacheRecord | undefined, now: number): string {
  if (!rec) return "";
  const age = Math.max(0, now - rec.stored_at * 1000);
  const stale = rec.ttl_ms > 0 && age >= rec.ttl_ms;
  return `cached ${humanAge(age)}${stale ? " (stale)" : ""}`;
}

function crumbBar(nav: Nav, storeLabel: (id: string) => string,
                  onGo: (n: Nav) => void): void {
  const trail = breadcrumb(nav, storeLabel);
  // Guarded like everything else on this screen. Nothing in a breadcrumb is transient,
  // but rebuilding it every second is still a link that can be missed mid-click.
  const sig = trail.map((c) => `${c.label}>${formatHash(c.nav)}`).join("|");
  if (crumbSig === sig) return;
  crumbSig = sig;
  const host = el("guided-crumbs");
  host.innerHTML = "";
  trail.forEach((c, i) => {
    const last = i === trail.length - 1;
    if (i) host.appendChild(document.createTextNode(" / "));
    if (last) {
      // The current screen is named but inert: a crumb that navigates to where you
      // already are reads as broken.
      const span = document.createElement("b");
      span.textContent = c.label;
      host.appendChild(span);
      return;
    }
    const a = document.createElement("a");
    a.href = formatHash(c.nav);
    a.textContent = c.label;
    a.onclick = (ev) => { ev.preventDefault(); onGo(c.nav); };
    host.appendChild(a);
  });
}

let crumbSig = "";

/** One clickable row. Plain elements rather than a table: these lists are short and a
 *  table's alignment buys nothing when every row is "a name and a couple of facts". */
function card(title: string, sub: string, onClick: () => void): HTMLElement {
  const b = document.createElement("button");
  b.className = "ghost";
  b.style.cssText = "display:block;width:100%;text-align:left;margin:0 0 6px;padding:8px 10px";
  b.innerHTML = `<b>${escapeHtml(title)}</b>` +
                (sub ? `<span class="mono" style="opacity:.7;margin-left:10px">${escapeHtml(sub)}</span>` : "");
  b.onclick = onClick;
  return b;
}

function note(host: HTMLElement, text: string): void {
  const d = document.createElement("div");
  d.style.cssText = "opacity:.7;margin:6px 0";
  d.textContent = text;
  host.appendChild(d);
}

export function renderGuided(
  nav: Nav, fleet: Fleet, state: DebugState, sites: string[], now: number,
  opts: GuidedOpts,
): void {
  const siteId = nav.siteId ?? "";
  const storeRec = siteId ? cachedAnswer(state, siteId, "store_list") : undefined;
  const nameOf = (id: string) => {
    const hit = objectsWith(storeRec?.data, "storeId")
      .find((o) => String(o.storeId) === storeIdFor(id));
    const n = hit?.storeName;
    return typeof n === "string" && n.trim() ? `${n} (${id})` : id;
  };

  crumbBar(nav, nameOf, opts.onGo);

  if (nav.view === "sites") {
    el("site-map").classList.add("hidden");
    // Cleared THROUGH repaint, not with a bare innerHTML: writing the element directly
    // would leave its recorded signature describing content that is no longer there, so
    // re-entering a site with an unchanged roster would skip the rebuild and show an
    // empty list. A cache is only correct while everything goes through it.
    repaint("guided-robots", "none", () => {});
    renderSites(nav, fleet, sites, opts);
    return;
  }
  renderSiteWorkspace(nav, fleet, state, siteId, now, opts);
}

/**
 * EVERY REGION HERE IS SIGNATURE-GUARDED, and the reason is a bug this screen shipped
 * with: `renderGuided` rebuilt its whole body on the one-second tick, so an open <select>
 * was destroyed under the operator's cursor about as fast as they could click it.
 *
 * The rule is the same one the vendor panel already follows and which I failed to carry
 * across: a signature describes STRUCTURE — which rows exist, which controls are shown —
 * and never contents. Live values are written into stable nodes with `.textContent` on
 * each tick instead, which touches no element the operator might be interacting with.
 */
function renderSites(nav: Nav, fleet: Fleet, sites: string[], opts: GuidedOpts): void {
  repaint("guided-body", `sites:${sites.join(",")}:${opts.error ?? ""}`, (host) => {
    if (opts.error) errorLine(host, opts.error);
    if (!sites.length) {
      // From the TOKEN, so empty means no grants — not "nothing has spoken yet".
      note(host, "Your account has no site grants, so there is nothing to show. "
               + "Ask an administrator for access.");
      return;
    }
    note(host, `${sites.length} site(s) your account can see. Every site the token `
             + `grants is listed, whether or not it has published anything yet.`);
    for (const id of sites) {
      const c = card(id, "", () => opts.onGo({ source: nav.source, view: "robots",
                                               siteId: id }));
      // A stable node for the live count, so the tick below never rebuilds this row.
      const sub = document.createElement("span");
      sub.id = `site-sub-${id}`;
      sub.className = "mono";
      sub.style.cssText = "opacity:.7;margin-left:10px";
      c.appendChild(sub);
      host.appendChild(c);
    }
  });

  for (const id of sites) {
    const sub = document.getElementById(`site-sub-${id}`);
    if (!sub) continue;
    const n = countRobots(fleet, id);
    const online = [...fleet.values()]
      .filter((r) => r.siteId === id && r.online !== false).length;
    sub.textContent = n ? `${online}/${n} online` : "no robots on the roster yet";
  }
}

function renderSiteWorkspace(
  nav: Nav, fleet: Fleet, state: DebugState, siteId: string, now: number,
  opts: GuidedOpts,
): void {
  const sceneRec = cachedAnswer(state, siteId, "scene_list");
  const scenes = objectsWith(sceneRec?.data, "sceneCode");
  const targets = readPoints(cachedAnswer(state, siteId, "scene_targets")?.data);
  const floors = floorsOf([...targets,
                           ...readPoints(cachedAnswer(state, siteId, "map_points_v2")?.data)]);

  // Structure only: which scenes exist, what is selected, which floors are offered.
  // NOT the cache age, which ticks, and not a robot count.
  const barSig = [siteId, scenes.map((o) => String(o.sceneCode)).join(","),
                  opts.scene ?? "", opts.floor ?? "", floors.join(","),
                  targets.length > 0, opts.error ?? ""].join("|");
  repaint("guided-body", `site:${barSig}`, (host) => {
    if (opts.error) errorLine(host, opts.error);

    const bar = document.createElement("div");
    bar.style.cssText = "display:flex;gap:10px;align-items:center;flex-wrap:wrap";

    if (!scenes.length) {
      const b = document.createElement("button");
      b.className = "primary";
      b.textContent = "Load scenes";
      b.title = "one vendor call, then cached for a day and shared with this site";
      b.onclick = opts.onLoadScenes;
      bar.append("Scene:", b);
      if (sceneRec) bar.append("(the scene list came back empty)");
    } else {
      const sel = document.createElement("select");
      sel.add(new Option("\u2014 none \u2014", ""));
      for (const o of scenes) {
        const code = String(o.sceneCode);
        const name = typeof o.sceneName === "string" && o.sceneName.trim()
          ? o.sceneName : code;
        sel.add(new Option(`${name} (${code})`, code));
      }
      sel.value = opts.scene ?? "";
      sel.onchange = () => opts.onScene(sel.value || null);
      bar.append("Scene:", sel);
      // Age lives in its own node and is written by the tick below: putting it in the
      // signature would rebuild this bar — and close this dropdown — every second.
      const chip = document.createElement("span");
      chip.id = "scene-chip";
      chip.className = "mono";
      chip.style.opacity = ".7";
      bar.appendChild(chip);
    }

    if (opts.scene) {
      const fsel = document.createElement("select");
      // NONE initially, showing no map: `map_fetch` is the most expensive call here and
      // a default floor would spend one for anybody who merely opened the site.
      fsel.add(new Option("\u2014 none \u2014", ""));
      for (const f of floors) fsel.add(new Option(f, f));
      fsel.value = opts.floor ?? "";
      fsel.onchange = () => opts.onFloor(fsel.value || null);
      bar.append("Floor:", fsel);
      if (!floors.length) {
        // Floors come from the scene's own points, so an empty list means those have not
        // arrived — not that the scene has one floor.
        bar.append(targets.length ? "(no floor ids in this scene's points)"
                                  : "(loading this scene's points\u2026)");
      }
    }
    host.appendChild(bar);

    if (opts.scene && !opts.floor) {
      note(host, "Pick a floor to load its map. That is one map_fetch, cached for a "
               + "week afterwards and shared with everyone on this site.");
    }

    // THE NAMED AREAS, listed rather than drawn — and the distinction is the vendor's,
    // not a shortcut. `area_list` returns `areaIdList` and `areaNameList` and nothing
    // else: no polygon, no bounding box, not even a centre point. Drawing an invented
    // shape for them would put something on the map the vendor never said was there.
    if (opts.floor) {
      const areaRec = cachedAnswer(state, siteId, "area_list");
      const areas = readAreas(areaRec?.data, opts.floor);
      const box = document.createElement("div");
      box.style.marginTop = "8px";
      if (areas.length) {
        box.innerHTML = `<b>Areas on floor ${escapeHtml(opts.floor)}</b> ` +
          `<span style="opacity:.6">(names only — the vendor sends no geometry, ` +
          `so these cannot be drawn on the map)</span><br>` +
          `<span class="mono">${areas.map((a) => escapeHtml(a.name)).join("  ·  ")}</span>`;
      } else {
        box.append(areaRec
          ? "No cleaning areas listed for this floor."
          : "");
        if (!areaRec) {
          const b = document.createElement("button");
          b.className = "ghost";
          b.textContent = "Load areas";
          b.title = "area_list — names only, no geometry";
          b.onclick = opts.onLoadAreas;
          box.append("Areas: ", b);
        }
      }
      host.appendChild(box);
    }
  });

  const chip = document.getElementById("scene-chip");
  if (chip) chip.textContent = cacheChip(sceneRec, now);

  // Read here and handed down, rather than read twice: the robot rows show the pixel a
  // position lands on, and a second reader could disagree with the map about the scale.
  const frame = readMapFrame(cachedAnswer(state, siteId, "map_fetch")?.data);
  renderSiteRobots(nav, fleet, siteId, now, { ...opts, frame });
}

function renderSiteRobots(nav: Nav, fleet: Fleet, siteId: string, now: number,
                          opts: GuidedOpts): void {
  const robots = sortedRobots(fleet).filter((r) => r.siteId === siteId);
  // WHICH robots and WHICH are ticked. Not their battery, mode or position — those are
  // exactly the values that change on the tick, and a checkbox rebuilt mid-click is a
  // checkbox that does not toggle.
  const sig = [siteId, robots.map((r) => r.robotId).join(","),
               [...opts.polled].sort().join(","), opts.pollSeconds].join("|");
  repaint("guided-robots", sig, (host) => {
    const head = document.createElement("b");
    head.textContent = "Robots";
    host.appendChild(head);

    if (!robots.length) {
      // The roster is retained, so an empty one means the adapter has not published one
      // — not that the site is empty.
      note(host, "No robots on the roster for this site yet. The roster is published by "
                + "the site's adapter and arrives on connect; if this stays empty, the "
                + "adapter has not published one.");
      return;
    }

    for (const r of robots) {
      const line = document.createElement("div");
      line.style.cssText = "display:flex;gap:8px;align-items:center;margin:4px 0";

      const box = document.createElement("input");
      box.type = "checkbox";
      box.checked = opts.polled.has(r.robotId);
      box.title = "poll this robot's location and show it on the map";
      box.onchange = () => opts.onPolled(r.robotId, box.checked);
      line.appendChild(box);

      const dot = document.createElement("span");
      dot.textContent = "\u25cf";
      dot.style.color = robotColour(r.robotId);
      dot.title = "this robot's colour on the map";
      line.appendChild(dot);

      const name = document.createElement("a");
      name.href = "#";
      name.textContent = r.robotId;
      name.onclick = (ev) => {
        ev.preventDefault();
        opts.onGo({ source: nav.source, view: "robot", siteId, robotId: r.robotId });
      };
      line.appendChild(name);

      const st = document.createElement("span");
      st.id = `rst-${r.robotId}`;
      st.className = "mono";
      st.style.opacity = ".75";
      line.appendChild(st);
      host.appendChild(line);
    }

    if (opts.polled.size) {
      // THE COST, stated. Each cycle is several vendor calls, and this vendor answers
      // over-polling by disabling the client_id rather than throttling.
      note(host, `Polling ${opts.polled.size} robot(s) every ${opts.pollSeconds}s. `
               + `Each cycle is several vendor calls; untick to stop.`);
    }
  });

  // The live half, written into stable nodes so nothing the operator is touching moves.
  for (const r of robots) {
    const st = document.getElementById(`rst-${r.robotId}`);
    if (!st) continue;
    // The position in full, including its frame and — once a map is loaded — the pixel
    // it lands on. The pixel is what makes a wrong scale or origin visible: a robot at a
    // plausible metre coordinate and an impossible pixel is a units bug, not a lost robot.
    const pos = r.position;
    const px = pos && opts.frame ? worldToPixel(opts.frame, pos.x, pos.y) : null;
    st.textContent = [
      r.online === false ? "offline" : "online",
      r.batteryPct !== undefined ? `${Math.round(r.batteryPct)}%` : "",
      r.mode ?? "",
      r.modeState === "paused" ? "paused" : "",
      pos ? `${pos.x.toFixed(2)}, ${pos.y.toFixed(2)} m` : "",
      // HOW OLD THE POSE IS, by the robot's own clock. The vendor's report can be
      // minutes behind the query that fetched it, and a moving robot drawn at a
      // five-minute-old position is worse than one not drawn at all.
      pos?.reportedAt ? `pose ${humanAge(now - pos.reportedAt * 1000)}` : "",
      // WHEN THE STATE WAS TRUE, not when we heard about it. The two differ because
      // polling is request-driven: a listing can arrive this second describing a robot
      // as it was twenty minutes ago, and showing only arrival would present that as
      // live. Carried over from the flat table this screen replaced, because the
      // distinction was the most valuable thing on it.
      `as of ${asOf(r, now)}`,
      pos?.frame ? `(${pos.frame})` : "",
      px ? `= px ${px.px.toFixed(0)}, ${px.py.toFixed(0)}` : "",
      // Absent for a REASON, derived from the vendor's own echo rather than guessed.
      // The one that matters is the vendor answering with nothing: no amount of polling
      // fixes that, and knowing it stops somebody hunting for a fault in the panel.
      poseWhy(r, opts.polled.has(r.robotId), now)?.text ?? "",
      isStale(r, now) ? "stale" : "",
    ].filter(Boolean).join("  \u00b7  ");
  }
}

function errorLine(host: HTMLElement, text: string): void {
  const e = document.createElement("div");
  e.className = "mono";
  e.style.color = "var(--bad)";
  e.textContent = text;
  host.appendChild(e);
}


function countRobots(fleet: Fleet, siteId: string): number {
  let n = 0;
  for (const r of fleet.values()) if (r.siteId === siteId) n++;
  return n;
}



// ---- commands (v2 only) ------------------------------------------------------
//
// HIDDEN ENTIRELY in the classic view. That view has never had an action surface, and
// the first place a robot can be moved from should be the one being reviewed, not the
// one people already use out of habit.
//
// The three gates live in commands.ts and are tested there. This renders them.

export interface ActionOpts {
  armed: boolean;
  confirming: string | null;
  onArm: (on: boolean) => void;
  onPress: (name: string) => void;
  /** Typed arguments, per operation. */
  values?: Record<string, Record<string, string>>;
  onField?: (action: string, field: string, value: string) => void;
  /** Picker options for an `x-ui` hint, so an operator chooses an area rather than
   *  typing a uuid. */
  pick?: (hint: string | undefined) => PickerOption[];
  /** Re-fetch the list behind a picker. */
  onRefreshOptions?: (hint: string) => void;
  /** Which operation's form is on screen. One at a time: seven forms at once is a wall,
   *  and three of them are schedule CRUD nobody uses in the same breath as "go clean". */
  selected?: string;
  onSelectCommand: (name: string) => void;
  /** What this panel tried to issue, newest first — sent or merely composed. */
  attempts?: CommandAttempt[];
  /** Whether this robot's location is being polled, and the control for it. Present on
   *  the drill-down as well as the list because this is the screen commands are sent
   *  from, and stale state is most dangerous exactly here. */
  polled?: boolean;
  onPolled?: (on: boolean) => void;
  pollSeconds?: number;
  /** Seconds left on the arming, so the control can show its own expiry rather than
   *  going dead without explanation. */
  armSecondsLeft?: number;
}

export function renderActions(robot: Robot | undefined, opts: ActionOpts,
                              visible: boolean): void {
  el("detail-actions").classList.toggle("hidden", !visible);
  if (!visible) return;

  const caps = robot?.capabilities ?? [];
  // WHAT STATE THE ROBOT IS IN, beside what it can do — because "not allowed in the
  // robot's current state" invites "well, what state IS it in?" and the panel has the
  // answer. A charging C40 is subState 32.
  const st = robot
    ? [robot.mode, robot.modeState !== robot.mode ? robot.modeState : "",
       robot.subState !== undefined ? `subState ${robot.subState}` : ""]
        .filter(Boolean).join(" \u00b7 ")
    : "";
  el("actions-source").textContent = caps.length
    ? `${robot?.platform ?? "adapter"} advertises ${caps.length} operation(s)`
      + (st ? `   \u2014   robot is ${st}` : "")
    : "";

  // THE MODE, stated before anything else and impossible to miss. A harness that looks
  // like the real thing teaches people the wrong lesson about what a button does.
  const banner = el("actions-mode");
  banner.textContent = COMMANDS_ENABLED
    ? "LIVE \u2014 these buttons command the robot"
    : "HARNESS \u2014 nothing is sent. The full arm/confirm path runs and the exact topic "
      + "and payload are shown below, but no command reaches the robot.";
  banner.style.cssText = COMMANDS_ENABLED
    ? "background:rgba(255,92,92,.12);border:1px solid rgba(255,92,92,.35);"
      + "color:var(--bad);padding:6px 8px;border-radius:4px;margin:6px 0;font-weight:700"
    : "background:rgba(75,139,255,.12);border:1px solid rgba(75,139,255,.35);"
      + "color:var(--fg);padding:6px 8px;border-radius:4px;margin:6px 0";

  const arm = el("actions-arm") as HTMLInputElement;
  // `.checked` is set rather than the element rebuilt, so the box does not flicker or
  // lose focus on the one-second tick.
  if (arm.checked !== opts.armed) arm.checked = opts.armed;
  arm.onchange = () => opts.onArm(arm.checked);

  const poll = el("actions-poll") as HTMLInputElement;
  if (poll.checked !== !!opts.polled) poll.checked = !!opts.polled;
  poll.onchange = () => opts.onPolled?.(poll.checked);
  el("actions-poll-note").textContent = opts.polled
    ? `polling every ${opts.pollSeconds ?? 15}s`
    : "not polling \u2014 state updates only when the vendor pushes";

  const views = actionViews(robot);
  // ONE COMMAND AT A TIME, chosen from a dropdown. Seven operations with their forms all
  // on screen at once is a wall, and three of them are schedule CRUD that nobody uses in
  // the same breath as "send it cleaning".
  const picked = views.find((v) => v.name === opts.selected) ?? views[0];

  // STRUCTURE ONLY. `missingArgs` was in here and changed from 1 to 0 as the operator
  // typed — rebuilding the form, and taking the caret with it, a second into every field.
  // That is the third time this app has lost focus to a signature carrying a live value;
  // the rule is that a signature describes what EXISTS, never what it contains.
  const sig = [opts.armed, opts.confirming ?? "", picked?.name ?? "",
               views.map((v) => `${v.name}:${v.state}`).join(",")].join("|");
  repaint("actions-body", sig, (host) => {
    if (!views.length) {
      const d = document.createElement("div");
      d.style.opacity = ".7";
      d.textContent = "This robot has not published a capability list, so nothing can be "
                    + "offered. That is the adapter's `status/capabilities`.";
      host.appendChild(d);
      return;
    }

    const bar = document.createElement("div");
    bar.style.cssText = "display:flex;gap:8px;align-items:center;margin-bottom:6px";
    const sel = document.createElement("select");
    for (const v of views) {
      const o = new Option(v.title, v.name);
      // The state on the option itself, so the cost of a choice is visible BEFORE it is
      // made rather than after the form has been filled in.
      if (v.state !== "ready") o.text = `${v.title}  \u2014 ${v.reason}`;
      sel.add(o);
    }
    sel.value = picked?.name ?? "";
    sel.onchange = () => opts.onSelectCommand(sel.value);
    bar.append("Command:", sel);
    host.appendChild(bar);

    if (!picked) return;

    if (picked.notes) {
      // Above the form: a caveat read after the arguments are typed is read too late.
      const n = document.createElement("div");
      n.className = "mono";
      n.style.cssText = "color:var(--warn);margin:8px 0 2px;font-size:12px";
      n.textContent = picked.notes;
      host.appendChild(n);
    }

    if (Object.keys(picked.params).length) {
      host.appendChild(callForm(
        { name: picked.name, enabled: true, params: picked.params,
          required: picked.required },
        opts.values?.[picked.name] ?? {}, null,
        (field, value) => opts.onField?.(picked.name, field, value),
        () => opts.onPress(picked.name),
        (hint) => opts.pick?.(hint) ?? [],
        false,
        opts.onRefreshOptions));
    }

    const b = document.createElement("button");
    b.id = "act-send";
    b.onclick = () => opts.onPress(picked.name);
    host.appendChild(b);

    // WHAT IS ABOUT TO HAPPEN, spelled out while the confirmation is up. The button can
    // only carry the verb and the robot; a cleaning run also has areas, passes and a
    // return point, and "confirm clean" without them asks somebody to approve a thing
    // they cannot see. Its own node, written below.
    const about = document.createElement("div");
    about.id = "act-about";
    about.className = "mono";
    about.style.cssText = "margin:4px 0 0;font-size:12px";
    host.appendChild(about);

    // The reasons, written out rather than left in tooltips: an explanation you can only
    // find by hovering has not been given.
    const blocked = views.filter((v) => v.state !== "ready");
    if (blocked.length) {
      const d = document.createElement("div");
      d.className = "mono";
      d.style.cssText = "opacity:.7;margin-top:6px";
      d.innerHTML = blocked
        .map((v) => `${escapeHtml(v.name)} \u2014 ${escapeHtml(v.reason)}`).join("<br>");
      host.appendChild(d);
    }
  });

  // THE LIVE HALF, outside the repaint: whether the send button is pressable depends on
  // what has been typed, and that must not rebuild anything.
  const send = document.getElementById("act-send") as HTMLButtonElement | null;
  if (send && picked) {
    const args = opts.values?.[picked.name] ?? {};
    const missing = missingArgs(picked, args);
    const confirming = opts.confirming === picked.name;
    const gated = picked.state !== "ready";
    send.disabled = !canPress(picked, args, opts.armed, COMMANDS_ENABLED);
    send.className = "act " + (
      send.disabled ? "" : confirming ? "confirm" : gated ? "gated" : "ready");
    send.textContent = confirming
      ? `Confirm ${picked.name} on ${robot?.robotId ?? "?"}`
      : gated && !COMMANDS_ENABLED ? `${picked.title} (robot would refuse)`
      : picked.title;
    const about = document.getElementById("act-about");
    if (about) {
      about.style.color = "var(--bad)";
      about.textContent = confirming
        ? `About to send ${picked.name} to ${robot?.robotId ?? "?"}`
          + (Object.keys(args).length ? ` with ${describeArgs(picked, args)}` : "")
          + ". Press again to confirm."
        : "";
    }
    send.title = missing.length
      ? `fill in: ${missing.join(", ")}`
      : !opts.armed ? "Enable controls first"
      : gated && !COMMANDS_ENABLED
      ? `${picked.reason} \u2014 the harness will still show what would be sent`
      : COMMANDS_ENABLED ? "Press twice to send"
      : "Press twice \u2014 the harness will show what WOULD be sent";
  }

  // Outcomes. `accepted` means the VENDOR took it, not that the robot did it, so the
  // phase is shown verbatim rather than translated into a tick.
  const events = (robot?.commands ?? []).slice(0, 5);
  const sim = (opts.attempts ?? []).slice(0, 5);
  const logSig = [events.map((e) => `${e.command_id ?? ""}${e.phase}`).join("|"),
                  sim.map((x) => `${x.at}${x.name}${x.sent}`).join("|")].join("::");
  repaint("actions-log", logSig, (host) => {
    // SIMULATED FIRST and visibly different. They are not something the robot said, and
    // a log that blurs the two would be worse than no log at all.
    for (const x of sim) {
      const d = document.createElement("div");
      d.style.cssText = "color:var(--fg);margin:2px 0";
      // SENT and WOULD-SEND are different facts and must not look alike: one moved a
      // machine and one did not.
      d.innerHTML = `<b style="color:${x.sent ? "var(--bad)" : "var(--fg)"}">` +
        `${x.sent ? "SENT" : "WOULD SEND"}</b> ${escapeHtml(x.name)}` +
        (x.wouldRefuse
          ? ` <span style="color:var(--bad)">\u2014 but the robot would have REFUSED: ` +
            `${escapeHtml(x.wouldRefuse)}</span>`
          : "") +
        `<div style="opacity:.75;word-break:break-all">${escapeHtml(x.topic)}<br>` +
        `${escapeHtml(JSON.stringify(x.payload))}</div>`;
      host.appendChild(d);
    }
    if (sim.length && events.length) {
      const sep = document.createElement("div");
      sep.style.cssText = "opacity:.5;margin:4px 0";
      sep.textContent = "\u2014 from the robot \u2014";
      host.appendChild(sep);
    }
    for (const e of events) {
      const d = document.createElement("div");
      const bad = e.phase === "failed" || e.phase === "rejected";
      d.style.color = bad ? "var(--bad)" : "";
      const code = e.code !== undefined ? ` [${e.code}]` : "";
      d.textContent = `${e.name} \u2014 ${e.phase}${code}${e.detail ? `: ${e.detail}` : ""}`;
      host.appendChild(d);
    }
    if (!sim.length && !events.length) {
      const d = document.createElement("div");
      d.style.opacity = ".6";
      d.textContent = "No commands yet. Outcomes arrive on telemetry/command, correlated "
                    + "by command_id \u2014 a refusal appears here and nowhere else.";
      host.appendChild(d);
    }
  });

  // The arming countdown, in its own node so it never rebuilds the buttons.
  const left = el("actions-arm-left");
  left.textContent = opts.armed && opts.armSecondsLeft !== undefined
    ? `disarms in ${opts.armSecondsLeft}s` : "";
}


// ---- the spatial view (v2 only, STOPGAP) -------------------------------------
//
// Plots two things in the VENDOR'S OWN FRAME: the points from a cached `map_points_v2`
// answer, and the robot's reported pose from `telemetry/localization`. Both come from
// the same vendor, so they are mutually consistent — but this is NOT a Norbot map and
// the heading says so. `telemetry_localization` carries a `frame`, and plotting a
// vendor frame on our map would put the robot somewhere meaningless.
//
// WHY NOT THE MAP PNG. `map_fetch` stays disabled. Three separate reasons, any one of
// which is sufficient: the result is a multi-megabyte RGBA PNG; EMQX's default
// `max_packet_size` is 1 MB, so it would most likely fail to publish at all; and
// `fleet/debug_result` is a broadcast channel every panel on the site receives and the
// recorder writes to disk. Rendering vector points costs none of that.
//
// The response SHAPE IS UNKNOWN — no `map_points_v2` sample has ever been captured, and
// `location` (the other coordinate-bearing call) has only ever returned null. So this
// reads tolerantly and, when it cannot find coordinates, says what it did receive
// instead of drawing an empty box.

/**
 * The vendor's map image, with the robot on it.
 *
 * Prefers the IMAGE when a `map_fetch` answer carries one, and falls back to plotting
 * `map_points_v2` points when it does not. Both are the vendor's own frame — the pose
 * arrives tagged `keenon_map`, explicitly not `map`, and the heading says so.
 *
 * THE RESOLUTION IS AN ASSUMPTION (4.2a: empirically 0.05 m/px, never sent by the
 * vendor). It is shown, every time, because a wrong one puts the robot in a plausible
 * place on a map of the wrong size — and the map's claimed extent in metres is shown
 * beside it, since a warehouse rendering as 5 m across is how that announces itself.
 */
export function renderMap(
  robot: Robot | undefined, state: DebugState, visible: boolean,
  /** Which map section to draw into. ONE renderer, two places — the robot drill-down and
   *  the site workspace — because two copies would be two sets of units to keep
   *  agreeing, and they would not. */
  prefix: "detail" | "site" = "detail",
  /** Extra dots to draw, each in its own colour: the robots being polled on the site
   *  screen. Empty in the drill-down, which shows exactly one robot — its own. */
  others: Array<{ id: string; colour: string; pos: NonNullable<Robot["position"]> }> = [],
  /** Only show points and targets for this floor, when one is selected. */
  floor: string | null = null,
): void {
  el(`${prefix}-map`).classList.toggle("hidden", !visible);
  if (!visible || !robot) return;

  const site = robot.siteId;
  const mapRec = cachedAnswer(state, site, "map_fetch");
  const mapResult = state.log.find(
    (p) => p.call === "map_fetch" && p.siteId === site && p.result?.ok)?.result;
  // The image reference lives on the RESULT, not in the cache: the cache holds the
  // vendor's metadata, and a URL expires, so the freshest result is the only thing that
  // can carry a usable link.
  const art = mapResult?.artifact;
  const rawFrame = readMapFrame(mapRec?.data ?? mapResult?.data);
  const ptsRec = cachedAnswer(state, site, "map_points_v2");
  // Filtered to the selected floor when there is one. A point from another floor drawn
  // on this map is not merely extra — it lands somewhere real-looking and wrong.
  const onFloor = (q: { floor?: string }) => !floor || !q.floor || q.floor === floor;
  const pts = readPoints(ptsRec?.data).filter(onFloor);
  // Targets are drawn too, in their own colour: they are where the robot is SENT, where
  // map points are where it can be. Showing them identically would lose that.
  // `scene_targets` carries NO COORDINATES — verified live: it returns pointId,
  // pointName, uuid, area and caseType and nothing spatial. So targets are named, not
  // placed, and the map does not pretend otherwise. The geometry is all in map_points_v2.
  const pose = robot.position;

  // The override, if somebody is comparing against a tape measure.
  const frame = rawFrame && (scaleOverride
    ? withResolution(rawFrame, scaleOverride.x, scaleOverride.y) : rawFrame);

  const bits: string[] = [];
  if (frame) {
    bits.push(wallPx ? `walls ${wallPx.wPx}x${wallPx.hPx}px`
                     : `canvas ${frame.width}x${frame.height}px`);
    bits.push(`${frame.resX} m/px` + (isSquare(frame) ? "" : ` x ${frame.resY} m/px`));
    if (wallPx) {
      bits.push(`~${(wallPx.wPx * frame.resX).toFixed(1)}` +
                `x${(wallPx.hPx * frame.resY).toFixed(1)}m`);
    }
  }
  if (pts.length) bits.push(`${pts.length} point(s)`);
  if (pose?.frame) bits.push(`"${pose.frame}"`);
  const why = IMAGE_WHY[mapImageState];
  if (why) bits.push(why);
  el(`${prefix}-map-summary`).textContent = bits.join("  ·  ");
  renderUnits(prefix, frame, pose);

  // Structure only. The pose moves continuously and must not be in here, or the canvas
  // is rebuilt every second and the image reloads with it.
  // `others` contributes its COUNT only: the positions move, and a moving value in a
  // paint signature rebuilds the canvas every tick.
  const sig = [art?.sha256 ?? "", frame ? `${frame.width}x${frame.height}` : "",
               pts.length, pose ? 1 : 0, others.length, floor ?? ""].join("|");
  repaint(`${prefix}-map-body`, sig, (host) => {
    if (art?.error) {
      note(host, `The adapter fetched an image but cannot serve it: ${art.error}`);
    }
    if (!art?.url && !pts.length) {
      note(host, mapRec || mapResult
        ? "A map answer is cached, but it carried neither a fetchable image nor "
          + "readable points. Open it in the vendor panel to see what came back."
        : "No map yet. Run map_fetch (and optionally map_points_v2) for this robot's "
          + "scene in the vendor panel; both are cached for a week afterwards.");
      return;
    }
    const c = document.createElement("canvas");
    c.width = 640;
    c.height = 440;
    c.style.cssText = "width:100%;max-width:640px;border:1px solid rgba(128,128,128,.3)";
    c.id = `${prefix}-map-canvas`;
    host.appendChild(c);
    // Interactions are wired ONCE per canvas, here, and the view state lives outside the
    // repaint — a pan or a half-finished measurement must survive a tick.
    wireCanvas(c, prefix, () => repaintMapNow());
    const readout = document.createElement("div");
    readout.id = `${prefix}-map-readout`;
    readout.className = "mono";
    readout.style.cssText = "margin-top:4px;font-size:12px;min-height:3em";
    host.appendChild(readout);
    if (art?.url) {
      const img = new Image();
      // The URL carries its own expiring token and the adapter answers `no-store`, so
      // there is nothing to gain from credentials and CORS stays simple.
      mapImageState = "loading";
      img.onload = () => {
        mapImage = img;
        mapImageState = "ok";
        // PIXELS, once. Metres are recomputed wherever they are shown, because the
        // scale is editable and a cached conversion would go stale the moment somebody
        // tried a different one.
        wallPx = measureWalls(img);
      };
      img.onerror = () => {
        // 410 is the NORMAL case after an adapter restart — the bytes live in memory
        // with no volume. Reported through the summary rather than appended here: this
        // fires after the repaint has finished, so anything added to `host` now would be
        // wiped by the next one and the explanation would come and go.
        mapImage = null;
        mapImageState = art.expires_at && art.expires_at * 1000 < Date.now()
          ? "expired" : "failed";
      };
      img.src = art.url;
    } else {
      mapImage = null;
      wallPx = null;
      mapImageState = "none";
    }
  });

  const canvas = document.getElementById(`${prefix}-map-canvas`) as HTMLCanvasElement | null;
  // Drawn OUTSIDE the repaint so the robot moves without the canvas being rebuilt and
  // the image re-requested. Same separation as the raw feed and the result log.
  if (canvas) {
    drawMap(canvas, prefix, pts, pose, frame, mapImage, others);
    renderReadout(prefix, frame);
  }
}

/** The decoded map image, once it has loaded. Module-level because it outlives a repaint
 *  and reloading it every tick would hammer an endpoint with an expiring token. */
let mapImage: HTMLImageElement | null = null;

/** Beyond this, a pose is ringed on the map and its age spelled out in the row. Half a
 *  minute is comfortably longer than a 15 s poll cycle, so a robot being polled normally
 *  never looks stale — only one whose vendor report has genuinely fallen behind. */
const STALE_POSE_S = 30;

/**
 * Why there is, or is not, a map image — SHOWN, never left to be inferred.
 *
 * With points but no image the screen simply drew the points, and an operator cannot tell
 * "not fetched" from "fetch failed" from "still loading" by looking at an absence.
 */
type ImageState = "none" | "loading" | "ok" | "expired" | "failed";
let mapImageState: ImageState = "none";

const IMAGE_WHY: Record<ImageState, string> = {
  none: "no map image — pick a floor, or run map_fetch for this scene",
  loading: "loading image\u2026",
  ok: "",
  // The adapter holds bytes in memory with no volume, so this is the normal state after
  // an adapter restart rather than an error.
  expired: "image link expired (adapter holds map bytes in memory) — re-run map_fetch",
  failed: "image could not be loaded",
};

/** The building's measured size, in PIXELS. Metres are recomputed wherever shown, since
 *  the scale is editable and a cached conversion would go stale. */
let wallPx: ReturnType<typeof occupiedExtentPx> = null;

// ---- the interactive view ----------------------------------------------------
//
// Pan, zoom and measurement, per map section. Kept out of the paint signature entirely:
// these change continuously while somebody is using them, and a repaint would both
// destroy the canvas and reset what they were measuring.

interface MapUi {
  view: View | null;
  /** The point under the cursor, for the readout. Set during the draw, which is the one
   *  place that knows where everything ended up on screen. */
  nearest?: Pt | null;
  /** Clicked measurement points, in IMAGE pixels. Only the last two are shown. */
  marks: Array<{ ix: number; iy: number }>;
  hover: { ix: number; iy: number } | null;
  drag: { x: number; y: number } | null;
}

const mapUi = new Map<string, MapUi>();
const uiFor = (prefix: string): MapUi => {
  let u = mapUi.get(prefix);
  if (!u) { u = { view: null, marks: [], hover: null, drag: null }; mapUi.set(prefix, u); }
  return u;
};

/**
 * Colours for the two measurement marks. Distinct from the robot palette and from the
 * points, so a mark is never mistaken for something the vendor put there.
 *
 * Chosen to read on BOTH surfaces, which is the constraint that rules out the obvious
 * choices: these are drawn on the canvas over a near-white map AND printed as text in the
 * readout on the app's dark panel. A deep red works on the map and disappears on the
 * panel; a pale one does the reverse. These mid-tones are legible on each.
 */
const MARK_COLOURS = ["#ff4d4d", "#a06bff"];

function wireCanvas(c: HTMLCanvasElement, prefix: string, redraw: () => void): void {
  const ui = uiFor(prefix);
  const at = (ev: MouseEvent) => {
    const r = c.getBoundingClientRect();
    // The canvas is CSS-scaled to fit its column, so client pixels are not canvas
    // pixels. Reading offsetX directly would put every coordinate out by that ratio —
    // and it would look like a calibration error rather than a units bug.
    return { x: (ev.clientX - r.left) * (c.width / r.width),
             y: (ev.clientY - r.top) * (c.height / r.height) };
  };

  c.onmousemove = (ev) => {
    const q = at(ev);
    if (ui.drag) {
      ui.view = ui.view && panBy(ui.view, q.x - ui.drag.x, q.y - ui.drag.y);
      ui.drag = q;
    }
    ui.hover = ui.view ? toImage(ui.view, q.x, q.y) : null;
    redraw();
  };
  c.onmouseleave = () => { ui.hover = null; ui.drag = null; redraw(); };
  c.onmousedown = (ev) => { ui.drag = at(ev); };
  c.onmouseup = (ev) => {
    const q = at(ev);
    const moved = ui.drag && Math.hypot(q.x - ui.drag.x, q.y - ui.drag.y) > 3;
    ui.drag = null;
    // A drag is not a click. Without this every pan would drop a measurement mark.
    if (!moved && ui.view) {
      ui.marks = [...ui.marks, toImage(ui.view, q.x, q.y)].slice(-2);
      redraw();
    }
  };
  c.onwheel = (ev) => {
    ev.preventDefault();          // the page must not scroll while zooming the map
    const q = at(ev);
    if (ui.view) ui.view = zoomAt(ui.view, q.x, q.y, ev.deltaY < 0 ? 1.15 : 1 / 1.15);
    redraw();
  };
  c.ondblclick = () => { ui.marks = []; ui.view = null; redraw(); };
  c.style.cursor = "crosshair";
  c.title = "wheel to zoom \u00b7 drag to pan \u00b7 click two points to measure \u00b7 "
          + "double-click to reset";
}

/**
 * Draw everything, in IMAGE-PIXEL space.
 *
 * The space matters more than anything else here. `map_points_v2` reports IMAGE PIXELS
 * (`positionX`/`positionY`), while `telemetry/localization` reports WORLD METRES — two
 * different spaces from the same vendor. Points are therefore plotted directly and the
 * robot pose goes through the frame. Treating the points as metres put them thousands of
 * pixels off-canvas, which looked exactly like "no points are rendered".
 */
function drawMap(c: HTMLCanvasElement, prefix: string, pts: Pt[],
                 pose: Robot["position"], frame: MapFrame | null,
                 image: HTMLImageElement | null,
                 others: Array<{ id: string; colour: string;
                                 pos: NonNullable<Robot["position"]> }> = []): void {
  const ctx = c.getContext("2d");
  if (!ctx) return;
  const ui = uiFor(prefix);

  const imgW = image?.naturalWidth || frame?.width || 0;
  const imgH = image?.naturalHeight || frame?.height || 0;
  if (!imgW || !imgH) return;
  if (!ui.view) ui.view = fitView(imgW, imgH, c.width, c.height);
  const v = ui.view;

  ctx.clearRect(0, 0, c.width, c.height);
  ctx.save();
  if (image) {
    ctx.imageSmoothingEnabled = false;     // zoomed-in map pixels should be square, not blurred
    ctx.drawImage(image, v.x, v.y, imgW * v.scale, imgH * v.scale);
  } else {
    // No image yet: show the extent so points and robots still have somewhere to sit.
    ctx.strokeStyle = "rgba(128,128,128,.5)";
    ctx.strokeRect(v.x, v.y, imgW * v.scale, imgH * v.scale);
  }

  const put = (ix: number, iy: number) => toCanvas(v, ix, iy);

  // The point under the cursor, if any. Found in CANVAS space so the grab radius stays
  // the same on screen however far in you have zoomed — a hit target that shrinks as you
  // zoom out is one you cannot hit when you most need to.
  const near = ui.hover ? nearestPoint(pts, v, ui.hover) : null;
  ui.nearest = near?.pt ?? null;

  for (const p of pts) {
    const q = put(p.x, p.y);
    const isNear = p === near?.pt;
    ctx.fillStyle = isNear ? "#05a" : "#08c";
    ctx.beginPath();
    ctx.arc(q.x, q.y, isNear ? 6 : 3, 0, Math.PI * 2);
    ctx.fill();
    if (isNear) {
      // Drawn on the map as well as in the readout: on a map with forty points, the one
      // you are pointing at is the question, and looking away to read the answer loses it.
      const text = `${p.label ?? "(unnamed)"}${p.kind ? ` [${p.kind}]` : ""}`;
      ctx.font = "12px system-ui, sans-serif";
      const w = ctx.measureText(text).width;
      ctx.fillStyle = "rgba(255,255,255,.85)";
      ctx.fillRect(q.x + 8, q.y - 18, w + 6, 16);
      ctx.fillStyle = "#05a";
      ctx.fillText(text, q.x + 11, q.y - 6);
    }
  }

  ctx.font = "11px system-ui, sans-serif";
  const drawRobot = (id: string, colour: string,
                     pos: NonNullable<Robot["position"]>) => {
    if (!frame) return;              // a pose without a frame cannot be placed at all
    const i = worldToPixel(frame, pos.x, pos.y);
    const q = put(i.px, i.py);
    const on = i.px >= 0 && i.px <= imgW && i.py >= 0 && i.py <= imgH;
    ctx.fillStyle = ctx.strokeStyle = colour;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(q.x, q.y, 6, 0, Math.PI * 2);
    // Outside the map it is drawn hollow, WHERE IT ACTUALLY IS. Clamping to the edge
    // would put it somewhere believable and wrong.
    if (on) ctx.fill(); else ctx.stroke();
    // A STALE POSE IS RINGED. The vendor's own report time can be minutes behind the
    // query, and a moving robot drawn at a five-minute-old position is worse than an
    // absent one — the operator has no way to tell it is not keeping up.
    const ageS = pos.reportedAt ? (Date.now() / 1000) - pos.reportedAt : 0;
    if (ageS > STALE_POSE_S) {
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(q.x, q.y, 11, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    const h = headingOnCanvas(pos.yaw);
    ctx.beginPath();
    ctx.moveTo(q.x, q.y);
    ctx.lineTo(q.x + h.dx * 16, q.y + h.dy * 16);
    ctx.stroke();
    if (id) ctx.fillText(id, q.x + 9, q.y - 7);
  };
  for (const o of others) drawRobot(o.id, o.colour, o.pos);
  if (pose) drawRobot("", "#0a7", pose);

  // The measurement marks and the line between them.
  ui.marks.forEach((m, i) => {
    const q = put(m.ix, m.iy);
    ctx.strokeStyle = ctx.fillStyle = MARK_COLOURS[i] ?? MARK_COLOURS[0];
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(q.x - 7, q.y);
    ctx.lineTo(q.x + 7, q.y);
    ctx.moveTo(q.x, q.y - 7);
    ctx.lineTo(q.x, q.y + 7);
    ctx.stroke();
  });
  if (ui.marks.length === 2) {
    const a = put(ui.marks[0].ix, ui.marks[0].iy);
    const b = put(ui.marks[1].ix, ui.marks[1].iy);
    ctx.strokeStyle = MARK_COLOURS[0];
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.restore();

  if (frame) drawScaleBar(ctx, c, frame, v.scale);
}

function measureWalls(img: HTMLImageElement) {
  try {
    const off = document.createElement("canvas");
    off.width = img.naturalWidth;
    off.height = img.naturalHeight;
    const ctx = off.getContext("2d", { willReadFrequently: false });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const d = ctx.getImageData(0, 0, off.width, off.height);
    return occupiedExtentPx(d.data, off.width, off.height);
  } catch {
    // getImageData taints on a cross-origin image without CORS headers. The map is worth
    // showing without its measurement, so this is a missing number rather than an error.
    return null;
  }
}

// ---- the units readout -------------------------------------------------------
//
// PIXELS FIRST, METRES SECOND, and the conversion between them stated and editable.
//
// A pixel span is a FACT about the image. A metre span is that fact multiplied by a
// number the vendor does not send. Presenting them with equal confidence is what let a
// correct scale look wrong and, worse, would let a wrong one look right. So the readout
// shows both, names the source of the scale, and lets it be overridden — because one
// tape measure across a known wall settles what no amount of arithmetic can.
//
// Squareness is called out for the same reason: `resY = 0.05` is forced by the origin
// arithmetic, but `resX` is only narrowed to {0.05, 0.10} and is pinned to 0.05 by a
// judgement about plausible building sizes. The two axes are therefore separately
// editable.

/** Per-session, in memory, applied to whatever map is on screen. Not persisted — this is
 *  an investigation aid, and a sticky override would quietly falsify a later reading. */
let scaleOverride: { x: number; y: number } | null = null;

function row(label: string, value: string, hint = ""): string {
  return `<tr><td style="opacity:.6;padding-right:12px">${escapeHtml(label)}</td>` +
         `<td>${escapeHtml(value)}</td>` +
         `<td style="opacity:.6;padding-left:12px">${escapeHtml(hint)}</td></tr>`;
}

function renderUnits(prefix: string, frame: MapFrame | null,
                     pose: Robot["position"]): void {
  const host = el(`${prefix}-map-units`);
  if (!frame) { host.innerHTML = ""; return; }

  const ext = extentMetres(frame);
  const origin = worldToPixel(frame, 0, 0);
  const sourceLabel = { vendor: "sent by the vendor", derived: "derived, not measured",
                        manual: "MANUAL OVERRIDE" }[frame.resSource];

  // Rebuilt only when something structural changes — never on the tick, and never
  // because the robot moved, or the two inputs below could not be typed into.
  const sig = [frame.width, frame.height, frame.resX, frame.resY, frame.resSource,
               wallPx?.wPx ?? -1, wallPx?.hPx ?? -1, pose ? 1 : 0].join("|");
  repaint(`${prefix}-map-units`, sig, (box) => {
    const t = document.createElement("table");
    t.style.cssText = "border-collapse:collapse;font-size:12px";
    const rows = [
      row("canvas", `${frame.width} x ${frame.height} px`,
          `${ext.w.toFixed(2)} x ${ext.h.toFixed(2)} m`),
    ];
    if (wallPx) {
      rows.push(row("walls", `${wallPx.wPx} x ${wallPx.hPx} px`,
                    `${(wallPx.wPx * frame.resX).toFixed(2)} x ` +
                    `${(wallPx.hPx * frame.resY).toFixed(2)} m  — measure this one`));
      rows.push(row("wall aspect", (wallPx.wPx / wallPx.hPx).toFixed(3),
                    "independent of scale; a scale cannot change it"));
    }
    rows.push(row("scale x", `${frame.resX} m/px`, sourceLabel));
    rows.push(row("scale y", `${frame.resY} m/px`,
                  isSquare(frame) ? "assumed equal to x (square pixels)"
                                  : "DIFFERENT from x — pixels are not square"));
    rows.push(row("origin", `${frame.originX.toFixed(3)}, ${frame.originY.toFixed(3)} m`,
                  `= pixel ${origin.px.toFixed(0)}, ${origin.py.toFixed(0)}`));
    if (pose) {
      const p = worldToPixel(frame, pose.x, pose.y);
      rows.push(row("robot", `${pose.x.toFixed(2)}, ${pose.y.toFixed(2)} m`,
                    `= pixel ${p.px.toFixed(0)}, ${p.py.toFixed(0)}` +
                    (inBounds(frame, p) ? "" : "  — OUTSIDE the map")));
    }
    t.innerHTML = rows.join("");
    box.appendChild(t);

    // The override. Two inputs and a square lock, because whether the axes share a
    // scale is precisely what is unproven.
    const form = document.createElement("div");
    form.style.cssText = "margin-top:6px;display:flex;gap:8px;align-items:center;flex-wrap:wrap";
    const mkInput = (value: number, on: (v: number) => void) => {
      const i = document.createElement("input");
      i.type = "number";
      i.step = "0.001";
      i.min = "0.001";
      i.value = String(value);
      i.style.width = "90px";
      i.onchange = () => {
        const v = Number(i.value);
        if (Number.isFinite(v) && v > 0) on(v);
      };
      return i;
    };
    const lock = document.createElement("input");
    lock.type = "checkbox";
    lock.checked = isSquare(frame);
    const xi = mkInput(frame.resX, (v) => {
      scaleOverride = { x: v, y: lock.checked ? v : frame.resY };
      repaintMapNow();
    });
    const yi = mkInput(frame.resY, (v) => {
      scaleOverride = { x: lock.checked ? v : frame.resX, y: v };
      repaintMapNow();
    });
    lock.onchange = () => { if (lock.checked) { yi.value = xi.value; xi.onchange!(new Event("change")); } };

    form.append("try m/px  x:", xi, "y:", yi);
    const lockLabel = document.createElement("label");
    lockLabel.append(lock, document.createTextNode(" square"));
    form.appendChild(lockLabel);

    const reset = document.createElement("button");
    reset.className = "ghost";
    reset.textContent = "reset";
    reset.onclick = () => { scaleOverride = null; repaintMapNow(); };
    form.appendChild(reset);
    box.appendChild(form);

    if (wallPx) {
      // The other direction: measure a wall, get the scale. This is the calculation
      // somebody would otherwise do on paper, and getting it backwards is easy.
      const calc = document.createElement("div");
      calc.style.cssText = "margin-top:4px;opacity:.75";
      const mi = document.createElement("input");
      mi.type = "number";
      mi.step = "0.1";
      mi.placeholder = "measured m";
      mi.style.width = "90px";
      const out = document.createElement("span");
      const recompute = () => {
        const m = Number(mi.value);
        const r = impliedResolution(wallPx!.wPx, m);
        out.textContent = r ? `  ->  ${r.toFixed(4)} m/px across ${wallPx!.wPx} px`
                            : "";
      };
      mi.oninput = recompute;
      calc.append("if the wall span measures ", mi, out);
      box.appendChild(calc);
    }
  });
}

/** Force the map to re-render after an override, without waiting for the tick. */
let repaintMapNow: () => void = () => {};
export function setMapRepaint(fn: () => void): void { repaintMapNow = fn; }

/** The point nearest the cursor, within a fixed CANVAS-pixel radius, or null. */
function nearestPoint(pts: Pt[], v: View, hover: { ix: number; iy: number }) {
  const GRAB_PX = 12;
  let best: { pt: Pt; d: number } | null = null;
  for (const pt of pts) {
    // Compared in canvas space: at 10x zoom two points a pixel apart in the image are
    // ten pixels apart on screen, and the grab radius should follow the screen.
    const d = Math.hypot((pt.x - hover.ix) * v.scale, (pt.y - hover.iy) * v.scale);
    if (d <= GRAB_PX && (!best || d < best.d)) best = { pt, d };
  }
  return best;
}

/**
 * Where the cursor is and what has been measured — in BOTH spaces, always.
 *
 * This is the instrument for settling the scale. A pixel position is a fact about the
 * image; the metres beside it are that fact times a number the vendor never sent. Putting
 * them side by side is what lets somebody stand in the room, click two points they can
 * measure, and read off whether 0.05 m/px is right.
 */
function renderReadout(prefix: string, frame: MapFrame | null): void {
  const host = document.getElementById(`${prefix}-map-readout`);
  if (!host) return;
  const ui = uiFor(prefix);
  const lines: string[] = [];

  const both = (ix: number, iy: number) => {
    const w = frame ? pixelToWorld(frame, ix, iy) : null;
    return `px ${ix.toFixed(1)}, ${iy.toFixed(1)}`
         + (w ? `   =   ${w.mx.toFixed(2)}, ${w.my.toFixed(2)} m` : "");
  };

  lines.push(ui.hover ? `cursor  ${both(ui.hover.ix, ui.hover.iy)}`
                      : "cursor  \u2014   (wheel zooms, drag pans, click to measure)");
  if (ui.nearest) {
    const n = ui.nearest;
    lines.push(`<b style="color:var(--accent)">point</b>  ${escapeHtml(n.label ?? "(unnamed)")}`
             + (n.kind ? `  [${escapeHtml(n.kind)}]` : "")
             + (n.floor ? `  floor ${escapeHtml(n.floor)}` : "")
             + `   ${both(n.x, n.y)}`);
  }
  ui.marks.forEach((m, i) => {
    lines.push(`<span style="color:${MARK_COLOURS[i] ?? MARK_COLOURS[0]}">\u2716</span> `
             + `mark ${i + 1}  ${both(m.ix, m.iy)}`);
  });
  if (ui.marks.length === 2) {
    const d = measure(frame, ui.marks[0], ui.marks[1]);
    const implied = d.m !== null && d.px > 0
      // The reverse question, answered inline: if the real distance between those two
      // points is known, this is the scale that would make it true.
      ? `   (a real span of X m implies X/${d.px.toFixed(1)} m/px)` : "";
    lines.push(`<b>distance  ${d.px.toFixed(1)} px`
             + (d.m !== null ? `   =   ${d.m.toFixed(3)} m` : "") + `</b>${implied}`);
  }
  host.innerHTML = lines.join("<br>");
}

/**
 * A metre bar, drawn on the map.
 *
 * The units table says the scale; this shows it. A bar that is visibly a third of a room
 * wide when it claims to be 5 m is the kind of error a number in a table does not
 * convey — and since the scale here is derived rather than sent, making it visible is
 * the point.
 *
 * Uses the X scale, and says so when the axes differ, because a single bar cannot
 * honestly represent two.
 */
function drawScaleBar(ctx: CanvasRenderingContext2D, c: HTMLCanvasElement,
                      frame: MapFrame, s: number): void {
  // A round number of metres that lands between a sixth and a third of the canvas, so
  // the bar is big enough to judge and small enough to fit.
  const target = c.width / 4;
  const choices = [1, 2, 5, 10, 20, 50];
  const metres = choices.find((m) => (m / frame.resX) * s >= target) ?? choices[choices.length - 1];
  const px = (metres / frame.resX) * s;
  const x = 12;
  const y = c.height - 14;

  ctx.save();
  // A light backing box, because this is drawn over TWO different surfaces: a near-white
  // map, and — before any image is fetched — the app's dark panel showing through. Black
  // on the second is invisible, and an invisible scale bar is worse than none, since its
  // absence is the only clue that the scale is unknown.
  ctx.fillStyle = "rgba(255,255,255,.85)";
  ctx.fillRect(x - 6, y - 24, px + 12, 32);
  ctx.strokeStyle = "#000";
  ctx.fillStyle = "#000";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + px, y);
  ctx.moveTo(x, y - 4);
  ctx.lineTo(x, y + 4);
  ctx.moveTo(x + px, y - 4);
  ctx.lineTo(x + px, y + 4);
  ctx.stroke();
  ctx.font = "11px system-ui, sans-serif";
  ctx.fillText(`${metres} m${isSquare(frame) ? "" : " (x axis)"}`, x, y - 8);
  ctx.restore();
}
